// src/pages/Expedientes/ExpedienteForm.js
import React, { useEffect, useState, useMemo } from "react";
import {
    TextField, Button, Dialog, DialogContent, DialogActions,
    Grid, Typography, Box, Chip, DialogTitle
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import Slide from "@mui/material/Slide";
import { Autocomplete } from '@mui/material';
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { capitalizar } from "../../utils/capitalizar";
import { getCombosJWT, expedienteAreasConjuntasJWT, agregarAreaConjuntaJWT, eliminarAreaConjuntaJWT } from "../../api/endpoints";
import AddIcon from "@mui/icons-material/Add";

const requiredStyle = { fontWeight: 600, color: "#d32f2f" };

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// ⭐ NUEVO: Convertir fecha de dd/MM/yyyy a yyyy-MM-dd
function convertirFechaParaInput(fechaStr) {
    if (!fechaStr || fechaStr === "-") return "";

    // Si ya viene en formato yyyy-MM-dd, devolverla tal cual
    if (fechaStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return fechaStr;
    }

    // Si viene en formato dd/MM/yyyy, convertir
    if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [dia, mes, anio] = fechaStr.split('/');
        return `${anio}-${mes}-${dia}`;
    }

    return "";
}

// -------- helpers carga/normalización (JWT) --------
async function loadCombo(comboName) {
    try {
        const data = await getCombosJWT(comboName);
        if (data?.ok && Array.isArray(data.items)) return data.items;
        if (Array.isArray(data)) return data;
        return [];
    } catch {
        return [];
    }
}

async function loadModulos() {
    try {
        const data = await getCombosJWT('modulos');
        if (data?.ok && Array.isArray(data.items)) return data.items;
        return [];
    } catch {
        return [];
    }
}

async function loadReparticiones() {
    try {
        const data = await getCombosJWT('reparticiones');
        if (data?.ok && Array.isArray(data.items)) return data.items;
        return [];
    } catch {
        return [];
    }
}

function toId(o) {
    if (o == null) return "";
    // ⭐ CORREGIDO: Dar prioridad a los campos en minúscula primero
    return String(
        o.id ?? o.Id ?? o.ID ?? o.value ?? o.Value ?? o.clave ?? o.Clave ?? o
    );
}

function toLabel(o, kind) {
    if (o == null) return "";
    if (typeof o === "string") return o;
    // PRIORIDAD: si el backend ya trae `label`, usarlo
    if (o.label != null) return String(o.label);

    // Fallbacks por tipo de combo
    switch (kind) {
        case "tipos_proceso":
            return (
                o.tipo ??
                o.Tipo ??
                o.Tipo_Proceso ??
                o.tipo_proceso ??
                o.nombre ??
                o.Nombre ??
                ""
            );
        case "estados_proceso":
            return (
                o.estado ??
                o.Estado ??
                o.Estados_Proceso ??
                o.nombre ??
                o.Nombre ??
                ""
            );
        case "objetos_reclamo":
            return (
                o.objeto_reclamo ??
                o.Objeto_Reclamo ??
                o.nombre ??
                o.Nombre ??
                ""
            );
        case "modulos":
            return (
                o.modulo ??
                o.Modulo ??
                o.nombre ??
                o.Nombre ??
                ""
            );
        case "reparticiones":
            return (
                o.reparticion ??
                o.Reparticion ??
                o.nombre ??
                o.Nombre ??
                ""
            );
        default:
            // genérico: soporta endpoints como api_tipo_movimientos (campo `tipo`)
            return (
                o.label ??
                o.tipo ??
                o.Tipo ??
                o.nombre ??
                o.Nombre ??
                ""
            );
    }
}

function normalizeCombo(arr, kind) {
    return (arr || [])
        .map((o) => {
            const Id = toId(o);
            const lbl = toLabel(o, kind);
            return {
                Id,
                label: capitalizar(String(lbl || "").trim()),
                raw: o
            };
        })
        // descartar placeholders tipo {id:0, label:""}
        .filter(x => !(String(x.Id) === "0" && x.label === ""));
}

export default function ExpedienteForm({ open, onClose, onSave, tipo = "judicial", expediente = null }) {
    const isJudicial = tipo === "judicial";
    const tipoColor = isJudicial ? "#1976d2" : "#d32f2f";
    const tipoIcon = isJudicial
        ? <GavelIcon style={{ color: tipoColor, fontSize: 36, marginRight: 12 }} />
        : <DescriptionIcon style={{ color: tipoColor, fontSize: 36, marginRight: 12 }} />;


    // Combos normalizados
    const [objetos, setObjetos] = useState([]);
    const [tiposProceso, setTiposProceso] = useState([]);
    const [estadosProceso, setEstadosProceso] = useState([]);
    const [modulos, setModulos] = useState([]);
    const [combosLoading, setCombosLoading] = useState(false);
    const [reparticiones, setReparticiones] = useState([]);

    // ⭐ NUEVO: Áreas conjuntas del expediente (para filtros)
    const [areasConjuntasExp, setAreasConjuntasExp] = useState([]);

    // ⭐ Estados para áreas conjuntas en el formulario
    const [dialogAreaConjunta, setDialogAreaConjunta] = useState(false);
    const [moduloSeleccionado, setModuloSeleccionado] = useState(null);

    const handleAgregarAreaConjunta = async () => {
        if (!moduloSeleccionado || !expediente?.id) {
            setDialogAreaConjunta(false);
            setModuloSeleccionado(null);
            return;
        }
        try {
            const resp = await agregarAreaConjuntaJWT(expediente.id, moduloSeleccionado);
            if (resp?.ok) {
                setAreasConjuntasExp(prev => [...prev, { id: resp.id, id_modulo: moduloSeleccionado }]);
            }
        } catch (err) {
            console.error("Error agregando área conjunta:", err);
        }
        setDialogAreaConjunta(false);
        setModuloSeleccionado(null);
    };

    const handleEliminarAreaConjunta = async (idArea) => {
        if (!expediente?.id) return;
        try {
            const resp = await eliminarAreaConjuntaJWT(expediente.id, idArea);
            if (resp?.ok) {
                setAreasConjuntasExp(prev => prev.filter(a => a.id !== idArea));
            }
        } catch (err) {
            console.error("Error eliminando área conjunta:", err);
        }
    };

    useEffect(() => {
        let alive = true;
        (async () => {
            setCombosLoading(true);
            const [objsRaw, tiposRaw, estadosRaw, modulosRaw, reparticionesRaw] = await Promise.all([
                loadCombo("objetos_reclamo"),
                loadCombo("tipos_proceso"),
                loadCombo("estados_proceso"),
                loadModulos(),
                loadReparticiones(), // ⭐ NUEVO
            ]);
            if (!alive) return;
            setObjetos(normalizeCombo(objsRaw, "objetos_reclamo"));
            setTiposProceso(normalizeCombo(tiposRaw, "tipos_proceso"));
            setEstadosProceso(normalizeCombo(estadosRaw, "estados_proceso"));
            setModulos(normalizeCombo(modulosRaw, "modulos"));
            setReparticiones(normalizeCombo(reparticionesRaw, "reparticiones")); // ⭐ NUEVO
            setCombosLoading(false);
        })();
        return () => { alive = false; };
    }, []);

    // Cargar áreas conjuntas del expediente al abrir
    useEffect(() => {
        if (!open || !expediente?.id) {
            setAreasConjuntasExp([]);
            return;
        }
        (async () => {
            try {
                const resp = await expedienteAreasConjuntasJWT(expediente.id);
                const items = resp?.items || resp?.areas || [];
                setAreasConjuntasExp(items.map(a => ({
                    id: a.id || a.Id,
                    id_modulo: a.id_modulo || a.Id_Modulo,
                    modulo: a.modulo || a.Modulo || '',
                })));
            } catch {
                setAreasConjuntasExp([]);
            }
        })();
    }, [open, expediente?.id]);

    // Filtrar módulos según tipo de expediente
    // ⭐ Filtrar módulos según tipo de expediente
    const modulosFiltrados = useMemo(() => {
        const excluidos = [6, 7, 8, 9, 15, 16];

        const filtered = modulos.filter(m => {
            const id = Number(m.Id);

            // Excluir los IDs marcados
            if (excluidos.includes(id)) {
                return false;
            }

            // Para Judiciales: permitir IDs 1-5, 10-14
            if (isJudicial) {
                const permitido = (id >= 1 && id <= 5) || (id >= 10 && id <= 14);
                return permitido;
            }

            // ⭐ Para Administrativos: permitir todos excepto excluidos
            return true;
        });

        return filtered;
    }, [modulos, isJudicial]); // ⭐ Agregué 'tipo' a las dependencias

    // Estado inicial (según lo acordado)
    const initialForm = isJudicial
        ? {
            caratula: "",
            autos_1_instancia: "",
            id_tipo_proceso: "",
            id_objeto_reclamo: "",
            id_estado: "",
            id_modulo: "",
            fecha_inicio: "", // ⭐ NUEVO
            fecha_asignacion: "", // ⭐ NUEVO
        }
        : {
            expediente_adm: "",
            extracto: "",
            iniciador: "",
            id_tipo_proceso: "",
            id_estado: "",
            id_objeto_reclamo: "",
            id_modulo: "",
            id_reparticion: "", // ⭐ NUEVO
            fecha_inicio: "", // ⭐ NUEVO
        };

    const [form, setForm] = useState(expediente ? { ...initialForm, ...expediente } : initialForm);
    const [errors, setErrors] = useState({});

    // Validaciones
    const validate = (name, value) => {
        let error = "";
        if (isJudicial) {
            // ⭐ MODIFICADO: Agregar id_modulo
            // "autos_1_instancia" no es obligatorio: a veces el expediente se carga
            // antes de tener ese dato (la planilla del juzgado llega después).
            if (["caratula", "id_tipo_proceso", "id_estado", "id_modulo"].includes(name) && !value) {
                error = "Obligatorio";
            }
        } else {
            // ⭐ MODIFICADO: Agregar id_modulo
            if (["expediente_adm", "extracto", "iniciador", "id_modulo"].includes(name) && !value) {
                error = "Obligatorio";
            }
        }
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        validate(name, value);
    };

    const handleSelect = (name, option) => {
        const value = option ? option.Id : "";
        setForm(prev => {
            const newForm = { ...prev, [name]: value };
            return newForm;
        });
        validate(name, value);
    };

    const isValid = () => {
        // ⭐ MODIFICADO: Agregar id_modulo
        const campos = isJudicial
            ? ["caratula", "id_tipo_proceso", "id_estado", "id_modulo"]
            : ["expediente_adm", "extracto", "iniciador", "id_modulo"];
        let ok = true;
        campos.forEach(c => {
            if (!form[c]) {
                setErrors(prev => ({ ...prev, [c]: "Obligatorio" }));
                ok = false;
            }
        });
        return ok;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!isValid()) return;

        if (isJudicial) {
            const dataToSend = {
                caratula: form.caratula,
                autos_1_instancia: form.autos_1_instancia,
                id_tipo_proceso: Number(form.id_tipo_proceso) || 0,
                id_objeto_reclamo: Number(form.id_objeto_reclamo) || 0,
                id_estado: Number(form.id_estado) || 0,
                id_modulo: Number(form.id_modulo) || 0,
                fecha_inicio: form.fecha_inicio || null, // ⭐ NUEVO
                fecha_asignacion: form.fecha_asignacion || null, // ⭐ NUEVO
                id: expediente?.id,
            };
            onSave(dataToSend);
        } else {
            const dataToSend = {
                expediente_adm: form.expediente_adm,
                extracto: form.extracto,
                iniciador: form.iniciador,
                id_tipo_proceso: Number(form.id_tipo_proceso) || 0,
                id_estado: Number(form.id_estado) || 0,
                id_objeto_reclamo: Number(form.id_objeto_reclamo) || 0,
                id_modulo: Number(form.id_modulo) || 0,
                id_reparticion: Number(form.id_reparticion) || 0, // ⭐ NUEVO
                fecha_inicio: form.fecha_inicio || null, // ⭐ NUEVO
                id: expediente?.id,
            };
            onSave(dataToSend);
        }
    };

    // Reset al cambiar tipo/expediente
    useEffect(() => {
        if (expediente) {
            const formNormalizado = {
                ...initialForm,
                ...expediente,
                // ⭐ Normalizar fechas
                fecha_inicio: convertirFechaParaInput(expediente.fecha_inicio || ""),
                fecha_asignacion: convertirFechaParaInput(expediente.fecha_asignacion || "")
            };
            setForm(formNormalizado);
        } else {
            setForm(initialForm);
        }
        setErrors({});
        // eslint-disable-next-line
    }, [tipo, expediente]);

    useEffect(() => {
        // Áreas conjuntas - endpoint no disponible aún en nuevo backend
        setAreasConjuntasExp([]);
    }, [expediente]);

    // Visibilidad - endpoint no disponible aún, mostrar todos los items sin filtrar
    const useVisibilidad = (tabla, idModuloOrigen, areasConjuntas) => {
        const [idsVisibles] = useState([]);
        const loading = false;

        // TODO: implementar endpoint /api/visibilidad en backend
        useEffect(() => {
        }, [tabla, idModuloOrigen, areasConjuntas]);

        return { idsVisibles, loading };
    };

    // ⭐ Filtros de visibilidad
    const { idsVisibles: idsObjetosVisibles } = useVisibilidad(
        'Objetos_Reclamo',
        form.id_modulo,
        areasConjuntasExp
    );

    const { idsVisibles: idsEstadosVisibles } = useVisibilidad(
        'Estados_Proceso',
        form.id_modulo,
        areasConjuntasExp
    );

    const { idsVisibles: idsReparticionesVisibles } = useVisibilidad(
        'Organismos_Reparticiones',
        form.id_modulo,
        areasConjuntasExp
    );

    // ⭐ Aplicar filtros a los combos
    const objetosFiltrados = useMemo(() => {
        if (!form.id_modulo || idsObjetosVisibles.length === 0) return objetos;
        return objetos.filter(o => idsObjetosVisibles.includes(Number(o.Id)));
    }, [objetos, idsObjetosVisibles, form.id_modulo]);

    const estadosFiltrados = useMemo(() => {
        if (!form.id_modulo || idsEstadosVisibles.length === 0) return estadosProceso;
        return estadosProceso.filter(e => idsEstadosVisibles.includes(Number(e.Id)));
    }, [estadosProceso, idsEstadosVisibles, form.id_modulo]);

    const reparticionesFiltradas = useMemo(() => {
        if (!isJudicial) { // Solo para administrativos
            if (!form.id_modulo || idsReparticionesVisibles.length === 0) return reparticiones;
            return reparticiones.filter(r => idsReparticionesVisibles.includes(Number(r.Id)));
        }
        return reparticiones;
    }, [reparticiones, idsReparticionesVisibles, form.id_modulo, isJudicial]);

    // Valor actual por Id
    const pickById = (arr, id) => (arr.find(o => String(o.Id) === String(id)) || null);
    const TPValue = pickById(tiposProceso, form.id_tipo_proceso);
    const ESTValue = pickById(estadosProceso, form.id_estado);
    const OBJValue = pickById(objetos, form.id_objeto_reclamo);
    const MODValue = pickById(modulos, form.id_modulo); // ⭐ NUEVO

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" TransitionComponent={Transition}>
            <Box display="flex" alignItems="center" px={3} pt={3} pb={1} borderBottom="1px solid #f0f0f0">
                {tipoIcon}
                <Box>
                    <Typography variant="h6" style={{ fontWeight: 700, color: "#222", lineHeight: 1.1 }}>
                        {expediente ? "Editar Expediente" : "Nuevo Expediente"}
                        <span style={{ color: tipoColor }}> – {isJudicial ? "Judicial" : "Administrativo"}</span>
                    </Typography>
                </Box>
            </Box>
            <DialogContent style={{ padding: 28, paddingBottom: 6 }}>
                <form onSubmit={handleSubmit} id="expediente-form">
                    <Typography variant="subtitle2" style={{ marginBottom: 8, color: "#444" }}>
                        Datos {isJudicial ? "obligatorios" : "obligatorios (los demás son opcionales)"}
                    </Typography>

                    <Grid container spacing={2} alignItems="flex-end">
                        {isJudicial ? (
                            <>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Carátula"
                                        name="caratula"
                                        value={capitalizar(form.caratula)}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.caratula}
                                        helperText={errors.caratula}
                                        autoFocus
                                        fullWidth
                                        InputLabelProps={{ style: requiredStyle }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.25, ml: 0.5 }}>
                                        Opcional — se puede completar después
                                    </Typography>
                                    <TextField
                                        label="Autos (1ra Instancia)"
                                        name="autos_1_instancia"
                                        value={form.autos_1_instancia}
                                        onChange={handleChange}
                                        error={!!errors.autos_1_instancia}
                                        helperText={errors.autos_1_instancia}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={tiposProceso}
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={TPValue}
                                        onChange={(_, v) => handleSelect("id_tipo_proceso", v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Tipo de Proceso"
                                                required
                                                error={!!errors.id_tipo_proceso}
                                                helperText={errors.id_tipo_proceso}
                                                InputLabelProps={{ style: requiredStyle }}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={estadosFiltrados}
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={ESTValue}
                                        onChange={(_, v) => handleSelect("id_estado", v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Situación"
                                                required
                                                error={!!errors.id_estado}
                                                helperText={errors.id_estado}
                                                InputLabelProps={{ style: requiredStyle }}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={modulosFiltrados}  
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={MODValue}
                                        onChange={(_, v) => handleSelect("id_modulo", v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Area Origen"
                                                required
                                                error={!!errors.id_modulo}
                                                helperText={errors.id_modulo}
                                                InputLabelProps={{ style: requiredStyle }}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* Objeto de Reclamo ya lo tenés */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={objetosFiltrados}
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={OBJValue}
                                        onChange={(_, v) => handleSelect("id_objeto_reclamo", v)}
                                        renderInput={params => (
                                            <TextField {...params} label="Objeto de Reclamo (opcional)" fullWidth />
                                        )}
                                    />
                                </Grid>

                                {/* ⭐ NUEVOS CAMPOS - Fechas */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Fecha Inicio"
                                        name="fecha_inicio"
                                        type="date"
                                        value={form.fecha_inicio}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Fecha de inicio del expediente"
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Fecha Asignación"
                                        name="fecha_asignacion"
                                        type="date"
                                        value={form.fecha_asignacion}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Fecha de asignación del expediente"
                                    />
                                </Grid>
                            </>
                        ) : (
                            <>
                                {/* Fila 1: Nº Expediente (req) / Extracto (req) */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="N° Expediente"
                                        name="expediente_adm"
                                        value={form.expediente_adm}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.expediente_adm}
                                        helperText={errors.expediente_adm}
                                        autoFocus
                                        fullWidth
                                        InputLabelProps={{ style: requiredStyle }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Extracto"
                                        name="extracto"
                                        value={capitalizar(form.extracto)}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.extracto}
                                        helperText={errors.extracto}
                                        fullWidth
                                        InputLabelProps={{ style: requiredStyle }}
                                    />
                                </Grid>

                                {/* Fila 2: Iniciador (req) / Area Origen (req) */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Iniciador"
                                        name="iniciador"
                                        value={capitalizar(form.iniciador)}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.iniciador}
                                        helperText={errors.iniciador}
                                        fullWidth
                                        InputLabelProps={{ style: requiredStyle }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={modulosFiltrados}
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={MODValue}
                                        onChange={(_, v) => handleSelect("id_modulo", v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Area Origen"
                                                required
                                                error={!!errors.id_modulo}
                                                helperText={errors.id_modulo}
                                                InputLabelProps={{ style: requiredStyle }}
                                                fullWidth
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* ⭐ Fila 3: Repartición Origen (opcional) / Fecha Inicio */}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Autocomplete
                                        options={reparticionesFiltradas}
                                        loading={combosLoading}
                                        getOptionLabel={(o) => (o?.label ?? "")}
                                        isOptionEqualToValue={(opt, val) => String(opt.Id) === String(val?.Id)}
                                        value={pickById(reparticionesFiltradas, form.id_reparticion)}
                                        onChange={(_, v) => handleSelect("id_reparticion", v)}
                                        renderInput={params => (
                                            <TextField
                                                {...params}
                                                label="Repartición Origen (opcional)"
                                                fullWidth
                                                helperText="Organismo o repartición de origen"
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Fecha Inicio"
                                        name="fecha_inicio"
                                        type="date"
                                        value={form.fecha_inicio}
                                        onChange={handleChange}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        helperText="Fecha de inicio del expediente"
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                    {/* ⭐ NUEVA SECCIÓN: Gestión de Áreas Conjuntas (solo al editar) */}
                    {expediente?.id && (
                        <Box mt={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                    <span role="img" aria-label="enlace">🔗</span> Áreas Conjuntas
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => setDialogAreaConjunta(true)}
                                    style={{ textTransform: 'none' }}
                                >
                                    Agregar Área
                                </Button>
                            </Box>

                            <Box display="flex" gap={1} flexWrap="wrap">
                                {areasConjuntasExp.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
                                        No hay áreas conjuntas asignadas
                                    </Typography>
                                ) : (
                                    areasConjuntasExp.map(area => (
                                        <Chip
                                            key={area.id}
                                            size="small"
                                            label={capitalizar(area.modulo)}
                                            onDelete={() => handleEliminarAreaConjunta(area.id)}
                                            style={{
                                                backgroundColor: isJudicial ? '#e3f2fd' : '#ffebee',
                                                color: isJudicial ? '#1976d2' : '#d32f2f',
                                                fontWeight: 600
                                            }}
                                        />
                                    ))
                                )}
                            </Box>
                        </Box>
                    )}
                    {/* Dialog para agregar área conjunta en el formulario */}
                    <Dialog open={dialogAreaConjunta} onClose={() => setDialogAreaConjunta(false)} maxWidth="xs" fullWidth>
                        <DialogTitle>Agregar Área Conjunta</DialogTitle>
                        <DialogContent>
                            <Box mt={2}>
                                <Autocomplete
                                    options={modulosFiltrados.filter(m => {
                                        // Filtrar módulo actual y áreas ya asignadas
                                        if (Number(m.Id) === Number(form.id_modulo)) return false;
                                        if (areasConjuntasExp.some(a => Number(a.id_modulo) === Number(m.Id))) return false;
                                        return true;
                                    })}
                                    loading={combosLoading}
                                    getOptionLabel={(o) => (o?.label ?? "")}
                                    value={modulosFiltrados.find(m => String(m.Id) === String(moduloSeleccionado)) || null}
                                    onChange={(_, v) => setModuloSeleccionado(v?.Id || null)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Seleccionar área"
                                            placeholder="Buscar área..."
                                            fullWidth
                                        />
                                    )}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDialogAreaConjunta(false)} color="secondary">
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleAgregarAreaConjunta}
                                color="primary"
                                variant="contained"
                                disabled={!moduloSeleccionado}
                            >
                                Agregar
                            </Button>
                        </DialogActions>
                    </Dialog>
                </form>
            </DialogContent>
            <DialogActions style={{ background: "#f5f5f5", padding: "20px 28px 24px 28px" }}>
                <Button onClick={onClose} color="secondary" variant="outlined"
                    startIcon={<CancelIcon />} style={{ fontWeight: 500, borderRadius: 4 }}>
                    Cancelar
                </Button>
                <Button type="submit" form="expediente-form" color="primary" variant="contained"
                    startIcon={<SaveIcon />} style={{ fontWeight: 700, borderRadius: 4, minWidth: 120, marginLeft: 10 }}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
